import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import React from "react";

import { IInsiderTileBadgeRendererProps } from "@faire/retailer/components/InsiderBadge/InsiderTileBadgeRenderer";
import { debugInfoSectionPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/DebugInfoSection/debugInfoSectionPropsILeanProductTileAdapter";
import { productTileFavoritePropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/Favorite/FavoriteProps";
import { searchProductImagePropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/search/SearchProductImageProps";
import { ClientFormatMoneyPriceWrapperProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/PriceWithClientFormatMoney";
import { productInfoClientWrapperAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductInfoClientWrapper/productInfoClientWrapperAdapter";
import { searchProductTileClickProviderPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileClickProvider/search/SearchProductTileClickProviderProps";
import { productTileTrackingWrapperPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapperProps";
import { productTileQuickAddPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { productTileTopBadgesPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TopBadges/TopBadgesProps";
import { LoggedOutSearchProductTile } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/LoggedOutSearchProductTile";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { PrehydrationClickTrackingWrapper } from "@faire/retailer/lib/nextjs-rsc/PrehydrationClickTrackingWrapper";
import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";
import { PRICE_TEXT_SURFACE } from "@faire/retailer/views/product/ProductDetails/Price/PriceProps";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useSearchPageIsRsc } from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageIsRsc";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useSearchPageSpellCorrectedQuery } from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageSpellCorrectedQuery";
import { useSearchPageProductSearchString } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { useSearchPageActionName } from "@faire/retailer/views/search-v2/util/useSearchPageActionName";

import { LoggedInSearchProductTile } from "./LoggedInSearchProductTile";
import { ISearchProductTile } from "./productTileTypes";
import { SearchProductTileProps } from "./SearchProductTileProps";

export const SearchProductTile: React.FC<ISearchProductTile> = (props) => {
  useTraceComponentRender("SearchProductTile", {
    productToken: props.productTile.product?.token,
  });
  const isLoggedIn = useIsLoggedInRetailerOrBrandPreview();

  const { productTile, brand } = props;
  const productSearchString = useSearchPageProductSearchString();
  const spellCorrectedQuery = useSearchPageSpellCorrectedQuery();

  const insiderBadgeProps: IInsiderTileBadgeRendererProps = {
    insiderBenefits: brand?.insider_benefits,
    insiderLabelText: brand?.insider_label?.text,
  };

  const searchPriceTextProps: ClientFormatMoneyPriceWrapperProps = {
    min_option_wholesale_price: productTile.min_option_wholesale_price,
    min_option_wholesale_promo_price:
      productTile.min_option_wholesale_promo_price,
    min_option_retail_price: productTile.min_option_retail_price,
    showProductPromotions: showProductPromotions(
      !productTile.is_locked_view
        ? productTile.min_option_wholesale_promo_price
        : undefined
    ),
    small: true,
    isLoggedInOrBrandPreview: isLoggedIn,
    isTilePricing: true,
    priceTextSurface: PRICE_TEXT_SURFACE.PRODUCT_TILE,
  };

  const searchProductTileProps: SearchProductTileProps = {
    productTileImageProps: searchProductImagePropsILeanProductTileAdapter(
      productTile,
      productSearchString,
      spellCorrectedQuery
    ),
    productTileTrackingWrapperProps:
      productTileTrackingWrapperPropsILeanProductTileAdapter(productTile),
    productTileDebugInfoProps:
      debugInfoSectionPropsILeanProductTileAdapter(productTile),
    productTileTopBadgesProps:
      productTileTopBadgesPropsILeanProductTileAdapter(productTile),
    productTileFavoriteProps:
      productTileFavoritePropsILeanProductTileAdapter(productTile),
    productTileQuickAddProps:
      productTileQuickAddPropsILeanProductTileAdapter(productTile),
    productTileClickProviderProps:
      searchProductTileClickProviderPropsILeanProductTileAdapter(productTile),
    priceTextProps: searchPriceTextProps,
    productTile,
    brand,
    insiderBadgeProps,
    productInfoClientWrapperProps: productInfoClientWrapperAdapter(productTile),
  };

  if (isLoggedIn) {
    return <LoggedInSearchProductTile data={searchProductTileProps} />;
  } else {
    return <LoggedOutSearchProductTile data={searchProductTileProps} />;
  }
};

export const SearchProductTilePrehydrationClickTrackingWrapper = ({
  productToken,
  children,
}: {
  productToken: string;
  children: React.ReactNode;
}) => {
  const actionName = useSearchPageActionName();
  const isRsc = useSearchPageIsRsc();
  return (
    <PrehydrationClickTrackingWrapper
      name={actionName}
      isRsc={isRsc}
      location={`product-tile-${productToken}`}
    >
      {children}
    </PrehydrationClickTrackingWrapper>
  );
};
